import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Award, FileText, Download, BookOpen, Star, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AthleteBooklet = () => {
  const [bookletData, setBookletData] = useState(null);
  const [creditsData, setCreditsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookletData();
    fetchCreditsData();
  }, []);

  const fetchBookletData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/my-athlete-booklet`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setBookletData(response.data);
    } catch (error) {
      console.error('Error fetching booklet:', error);
      toast.error('Errore nel caricamento del libretto');
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditsData = async () => {
    try {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      
      if (!userStr) {
        console.error('No user found in localStorage');
        return;
      }
      
      const user = JSON.parse(userStr);
      
      if (!user || !user.id) {
        console.error('Invalid user data');
        return;
      }
      
      const response = await axios.get(`${API}/athlete/${user.id}/credits-summary`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setCreditsData(response.data);
    } catch (error) {
      console.error('Error fetching credits:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToPDF = () => {
    if (!bookletData) {
      toast.error('Nessun dato disponibile per il PDF');
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPos = 20;

      // Load and add logos
      const logoImg = new Image();
      logoImg.src = '/cibs-logo.png';
      
      // Add logo on left (convert 50x50 pixels to mm - assuming 96 DPI)
      const logoSize = 15; // 15mm approximately 50px at 96 DPI
      const leftLogoX = 15;
      const rightLogoX = pageWidth - 15 - logoSize;
      const logoY = yPos - 5;
      
      try {
        doc.addImage(logoImg, 'PNG', leftLogoX, logoY, logoSize, logoSize);
        doc.addImage(logoImg, 'PNG', rightLogoX, logoY, logoSize, logoSize);
      } catch (error) {
        console.warn('Could not add logo to PDF:', error);
      }

    // Title
    doc.setFontSize(20);
    doc.setTextColor(147, 51, 234); // Purple
    doc.text('LIBRETTO ATLETA', pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Athlete Info
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(`Atleta: ${bookletData.athlete_name}`, 15, yPos);
    yPos += 7;
    
    if (bookletData.athlete_number) {
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(`Numero Atleta: ${bookletData.athlete_number}`, 15, yPos);
      yPos += 7;
    }

    // Athlete Personal Data Section
    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    
    if (bookletData.birth_date) {
      doc.text(`Data di Nascita: ${bookletData.birth_date}`, 15, yPos);
      yPos += 6;
    }
    
    if (bookletData.fiscal_code) {
      doc.text(`Codice Fiscale: ${bookletData.fiscal_code}`, 15, yPos);
      yPos += 6;
    }
    
    if (bookletData.athlete_class) {
      doc.text(`Classe di Appartenenza: ${bookletData.athlete_class}`, 15, yPos);
      yPos += 6;
    }
    
    if (bookletData.asa_reference) {
      doc.text(`ASA di Riferimento: ${bookletData.asa_reference}`, 15, yPos);
      yPos += 6;
    }
    
    yPos += 5;

    // Summary
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(`Eventi Partecipati: ${bookletData.total_events}`, 15, yPos);
    yPos += 5;
    doc.text(`Match Analysis: ${bookletData.total_match_analysis}`, 15, yPos);
    yPos += 5;
    doc.text(`Match Analysis Dettagliate: ${bookletData.total_detailed_analysis}`, 15, yPos);
    yPos += 15;

    // Items Table
    if (bookletData.items && bookletData.items.length > 0) {
      const tableData = bookletData.items.map(item => {
        if (item.type === 'event') {
          return [
            formatDate(item.date),
            'Evento',
            item.event_title || '-',
            item.location || '-',
            '-',
            ''
          ];
        } else if (item.type === 'match_analysis') {
          return [
            formatDate(item.date),
            'Match Analysis',
            item.event_title || '-',
            item.teacher_name || '-',
            '-',
            ''
          ];
        } else if (item.type === 'detailed_match_analysis') {
          return [
            formatDate(item.date),
            'Match Analysis Dettagliata',
            item.event_title || '-',
            item.teacher_name || '-',
            `${item.total_score || 0}/10`,
            item.notes || ''
          ];
        } else if (item.type === 'technical_credit') {
          return [
            formatDate(item.date),
            'Credito Tecnico',
            `A.A. ${item.academic_year}`,
            'ADMIN CIBS',
            `${item.credits} crediti`,
            item.notes || ''
          ];
        }
        return ['', '', '', '', '', ''];
      }).filter(row => row[0] !== '');

      autoTable(doc, {
        startY: yPos,
        head: [['Data', 'Tipo', 'Evento', 'Teacher/Luogo', 'Punteggio', 'Note']],
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [147, 51, 234],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        styles: {
          fontSize: 8,
          cellPadding: 3
        },
        columnStyles: {
          0: { cellWidth: 22 },
          1: { cellWidth: 28 },
          2: { cellWidth: 45 },
          3: { cellWidth: 30 },
          4: { cellWidth: 22 },
          5: { cellWidth: 38 }
        }
      });
    }

    // Credits Summary Section
    let creditsY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : yPos + 10;
    
    // Check if we need a new page
    if (creditsY > pageHeight - 80) {
      doc.addPage();
      creditsY = 20;
    }

    doc.setFontSize(14);
    doc.setTextColor(147, 51, 234);
    doc.text('RIEPILOGO CREDITI E VOTO FINALE', pageWidth / 2, creditsY, { align: 'center' });
    creditsY += 10;

    if (creditsData && creditsData.academic_years && creditsData.academic_years.length > 0) {
      const creditsTableData = creditsData.academic_years.map(year => [
        year.academic_year,
        `${year.formative_credits} (${year.events_count} eventi)`,
        String(year.technical_credits),
        String(year.total_credits),
        year.final_grade || year.grade || '-'
      ]);

      autoTable(doc, {
        startY: creditsY,
        head: [['Anno Accademico', 'Crediti Formativi', 'Crediti Tecnici', 'Totale', 'Voto Finale']],
        body: creditsTableData,
        theme: 'grid',
        headStyles: {
          fillColor: [234, 179, 8],
          textColor: [0, 0, 0],
          fontStyle: 'bold'
        },
        styles: {
          fontSize: 10,
          cellPadding: 4,
          halign: 'center'
        },
        columnStyles: {
          4: { fontStyle: 'bold', textColor: [88, 28, 135] }
        }
      });
    } else {
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('Nessun credito accumulato', pageWidth / 2, creditsY, { align: 'center' });
    }

    // Footer
    const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : yPos;
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Generato il ${formatDateTime(new Date())}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );

    // Save
    const fileName = `Libretto_Atleta_${bookletData.athlete_name.replace(/ /g, '_')}.pdf`;
    doc.save(fileName);
    toast.success('PDF scaricato con successo!');
    } catch (error) {
      console.error('Errore generazione PDF:', error);
      toast.error('Errore nella generazione del PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!bookletData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Impossibile caricare il libretto atleta</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <BookOpen className="w-6 h-6 text-red-600" />
                Libretto Atleta
              </CardTitle>
              <CardDescription className="mt-2">
                Riepilogo completo di tutte le tue attività e valutazioni
              </CardDescription>
            </div>
            <Button
              onClick={exportToPDF}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Scarica PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <Calendar className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-600">{bookletData.total_events}</p>
              <p className="text-sm text-gray-600">Eventi Partecipati</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <FileText className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-600">{bookletData.total_match_analysis}</p>
              <p className="text-sm text-gray-600">Match Analysis</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <Award className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-purple-600">{bookletData.total_detailed_analysis}</p>
              <p className="text-sm text-gray-600">Analisi Dettagliate</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <Star className="w-8 h-8 text-red-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-red-600">
                {bookletData.total_events + bookletData.total_match_analysis + bookletData.total_detailed_analysis}
              </p>
              <p className="text-sm text-gray-600">Totale Attività</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Credits Section */}
      <Card className="border-2 border-yellow-400">
        <CardHeader className="bg-gradient-to-r from-yellow-50 to-orange-50">
          <CardTitle className="flex items-center space-x-2">
            <Award className="w-6 h-6 text-yellow-600" />
            <span>Crediti Formativi e Tecnici</span>
          </CardTitle>
          <CardDescription>
            Riepilogo dei crediti accumulati per anno accademico
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {creditsData && creditsData.academic_years && creditsData.academic_years.length > 0 ? (
              <div className="space-y-6">
                {creditsData.academic_years.map((yearData, index) => (
                  <div key={index} className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                      Anno Accademico {yearData.academic_year}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {/* Crediti Formativi */}
                      <div className="bg-orange-500 text-white rounded-lg p-4 shadow-md">
                        <div className="text-center">
                          <p className="text-sm font-medium mb-2">CREDITI FORMATIVI</p>
                          <p className="text-3xl font-bold">{yearData.formative_credits}</p>
                          <p className="text-xs mt-2 opacity-90">
                            {yearData.events_count} {yearData.events_count === 1 ? 'evento' : 'eventi'}
                          </p>
                        </div>
                      </div>

                      {/* Crediti Tecnici */}
                      <div className="bg-cyan-500 text-white rounded-lg p-4 shadow-md">
                        <div className="text-center">
                          <p className="text-sm font-medium mb-2">CREDITI TECNICI</p>
                          <p className="text-3xl font-bold">{yearData.technical_credits}</p>
                          <p className="text-xs mt-2 opacity-90">
                            Assegnati da Admin
                          </p>
                        </div>
                      </div>

                      {/* Totale */}
                      <div className="bg-white border-4 border-green-500 rounded-lg p-4 shadow-md">
                        <div className="text-center">
                          <p className="text-sm font-medium text-green-700 mb-2">TOTALE</p>
                          <p className="text-3xl font-bold text-green-600">{yearData.total_credits}</p>
                          <p className="text-xs mt-2 text-gray-600">
                            Crediti totali
                          </p>
                        </div>
                      </div>

                      {/* Voto */}
                      <div className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white rounded-lg p-4 shadow-md">
                        <div className="text-center flex flex-col justify-center h-full">
                          <p className="text-sm font-medium mb-2">VOTO FINALE</p>
                          <p className={`text-lg font-bold leading-tight ${
                            yearData.final_grade === 'ECCELLENTE' ? 'text-yellow-300' :
                            yearData.final_grade === 'OTTIMO' ? 'text-green-300' :
                            yearData.final_grade === 'BUONO' ? 'text-blue-300' :
                            yearData.final_grade === 'PIENAMENTE SUFFICIENTE' ? 'text-cyan-300' :
                            yearData.final_grade === 'SUFFICIENTE' ? 'text-orange-300' :
                            'text-gray-300'
                          }`}>
                            {yearData.final_grade}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <Award className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  Nessun credito accumulato
                </h3>
                <p className="text-gray-600">
                  Partecipa agli eventi per iniziare ad accumulare crediti formativi!
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Anno Accademico corrente: <strong>2025/2026</strong>
                </p>
              </div>
            )}
        </CardContent>
      </Card>

      {/* Activity Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Cronologia Attività</CardTitle>
          <CardDescription>
            Lista completa delle tue partecipazioni e valutazioni
          </CardDescription>
        </CardHeader>
        <CardContent>
          {bookletData.items && bookletData.items.length > 0 ? (
            <div className="space-y-4">
              {bookletData.items.map((item, index) => (
                <div
                  key={index}
                  className="border-l-4 border-red-600 pl-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {item.type === 'event' && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            <Calendar className="w-3 h-3 mr-1" />
                            Evento
                          </Badge>
                        )}
                        {item.type === 'match_analysis' && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <FileText className="w-3 h-3 mr-1" />
                            Match Analysis
                          </Badge>
                        )}
                        {item.type === 'detailed_match_analysis' && (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                            <Award className="w-3 h-3 mr-1" />
                            Analisi Dettagliata
                          </Badge>
                        )}
                        {item.type === 'technical_credit' && (
                          <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200">
                            <TrendingUp className="w-3 h-3 mr-1" />
                            Credito Tecnico
                          </Badge>
                        )}
                        <span className="text-sm text-gray-500">{formatDate(item.date)}</span>
                      </div>
                      
                      <h3 className="font-semibold text-lg">
                        {item.type === 'technical_credit' 
                          ? `Credito Tecnico - A.A. ${item.academic_year}` 
                          : item.event_title}
                      </h3>
                      
                      {item.type === 'event' && item.location && (
                        <p className="text-sm text-gray-600 mt-1">
                          📍 Luogo: {item.location}
                        </p>
                      )}
                      
                      {(item.type === 'match_analysis' || item.type === 'detailed_match_analysis') && (
                        <p className="text-sm text-gray-600 mt-1">
                          👤 Valutato da: <span className="font-medium">{item.teacher_name}</span>
                        </p>
                      )}
                      
                      {item.type === 'match_analysis' && item.content && (
                        <div className="mt-2 p-3 bg-gray-50 rounded text-sm">
                          <p className="text-gray-700">{item.content}</p>
                        </div>
                      )}
                      
                      {item.type === 'detailed_match_analysis' && (
                        <div className="mt-2 space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">Punteggio Totale:</span>
                            <Badge className="bg-purple-600 text-white">
                              {item.total_score}/10
                            </Badge>
                          </div>
                          {item.notes && (
                            <div className="p-3 bg-gray-50 rounded text-sm">
                              <p className="text-gray-700">{item.notes}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {item.type === 'technical_credit' && (
                        <div className="mt-2 space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">Crediti Assegnati:</span>
                            <Badge className="bg-cyan-600 text-white">
                              {item.credits} crediti
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600">
                            Anno Accademico: <span className="font-medium">{item.academic_year}</span>
                          </p>
                          <p className="text-sm text-gray-600">
                            Assegnati da: <span className="font-medium">{item.assigned_by_name}</span>
                          </p>
                          {item.notes && (
                            <div className="p-3 bg-gray-50 rounded text-sm">
                              <p className="text-gray-700">{item.notes}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Nessuna attività registrata</p>
              <p className="text-sm text-gray-400 mt-2">
                Le tue partecipazioni agli eventi e le valutazioni ricevute appariranno qui
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AthleteBooklet;
